import {
  Component, ElementRef, Input, OnInit, ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';

import {ProductSelectorComponent} from '../product-selector/product-selector.component';
import {Project} from '../../../shared/models/project';
import {Opening} from '../../../shared/models/opening';
import {Size} from '../../../shared/models/size';
import {MatDialog} from '@angular/material/dialog';
import {YesNoDialogComponent} from '../../../yesno-dialog/yesno-dialog.component';
import {MatDialogConfig} from '@angular/material/dialog';
import {FiltersDialogComponent} from '../../filters-dialog/filters-dialog.component';
import {Location} from '@angular/common';
import {environment} from '../../../../environments/environment';
import {Shape} from '../../../shared/models/shape';
import {BreakpointObserver} from "@angular/cdk/layout";
import {PartDisplayService} from "../../../shared/services/part-display.service";

@Component({
    selector: 'mbnm-mat-selector',
    templateUrl: './mat-selector.component.html',
    styleUrls: ['./mat-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MatSelectorComponent extends ProductSelectorComponent implements OnInit {

  constructor(public media: BreakpointObserver,
              public dialog: MatDialog,
              private location: Location,
              partDisplay: PartDisplayService) {
    super(media, partDisplay);
  }

  currentTabIndex = 0;
  currentMatOptions: any;
  vGrooveYesNo = false;

  @Input()
  project: Project;

  @Input()
  layers: any;

  ngOnInit() {
    if (this.productCategory.filterOptions) {
      this.currentMatOptions = {
        quality: this.productCategory.filterOptions[0].selection,
        coreType: this.productCategory.filterOptions[1].selection,
        thickness: this.productCategory.filterOptions[2].selection,
      };
    }

    this.groupedProducts = this.group(this.productCategory.filteredProducts ? this.productCategory.filteredProducts : this.productCategory.products);
    this.availableProductsChanged.emit({
      numProductGroups: this.groupedProducts.length,
      allSelected: false,
      missingDependency: this.missingDependency
    });

    this.vGrooveYesNo = this.project.vGrooveOffset > 0;
  }

  onTabChanged($event) {
    this.currentTabIndex = $event.index;
  }

  get currentMat(): any {
    return this.project[this.layers[this.currentTabIndex].projectProperty];
  }

  set currentMat(value: any) {
    this.project[this.layers[this.currentTabIndex].projectProperty] = value;
  }

  chooseMat($event, selection: any) {
    $event.stopPropagation();
    if (selection.active) {
      if (!this.samplesMode) this.currentMat = selection;
      super.choose(selection);
    } else {
      const dialogRef = this.dialog.open(YesNoDialogComponent, {});
      const instance = dialogRef.componentInstance;
      instance.data = {
        title: 'Temporarily Out of Stock',
        message: '<p>This selection is temporarily out of stock. Please pick another item.</p>',
        yesButtonText: 'OK',
        noButtonText: null
      };
    }
  }

  @ViewChild('products') products: ElementRef;

  public resetPosition(): void {
    this.products.nativeElement.scrollTo(0, 0);
  }

  addOpening($event) {
    $event.stopPropagation();
    const openingSize = this.project.size.incomplete ? new Size(NaN, NaN) : Project.ensureRealSize(new Size(this.project.size.width * 0.5625, this.project.size.height * 0.65));
    const opening = new Opening(openingSize, [Shape.DEFAULT]);
    this.project.clearOpenings();
    this.project.addOpening(opening);
    this.project.bottomMargin = this.project.centeredMargins.height;
  }

  infoData: any = {
    layers: {
      closeMethod: 'x',
      title: 'Primary & Accent Mats',
      message: '<p>The top most visible layer is called the primary mat. The optional accent layer below it is called the bottom mat.</p>' +
      '<p>Having an accent mat does not change your opening, but instead reveals itself by reducing the top mat margin.  The default amount of \"reveal\" is 1/4\".</p>' +
      '<p class="image"><img src="/assets/images/help/mat-layers.png" alt="Matboard Layers"/></p>'
    },
    types: {
      closeMethod: 'x',
      title: 'Which Mat Do I Need?',
      message: '<p>It depends on what you are matting, and your personal tastes.  Your mat type is determined by it\'s quality, core color and thickness.</p>' +
      '<p><strong>Quality</strong> determines what application it\'s suitable for, such as posters vs fine art that requires conservation materials.</p>' +
      '<p><strong>Core Color</strong> refers to the inside or \'core\' of the mat when it is cut.</p>' +
      '<p><strong>Thickness</strong> of our mats are 4 ply & 8 ply which are the most common/standard mat thicknesses.</p>'
    },
    vGroove: {
      closeMethod: 'x',
      title: 'What is a V-Groove?',
      message: '<p>This refers to a shallow cut across the mat which exposes the core color of the primary mat. The standard size is typically 1/2" offset from the opening but can be customized if desired.</p>' +
        '<p class="image shadow"><img src="/assets/images/help/v-groove.jpg" alt="Matboard V-Groove"/></p>'
    },
    reveal: {
      closeMethod: 'x',
      title: 'What is a Reveal?',
      message: '<p>This refers to how wide the accent mat is. The standard size is typically 1/4" but you can choose to increase that to 3/8" or 1/2" if desired.</p>' +
        '<p class="image shadow"><img src="/assets/images/help/reveal.jpg" alt="Matboard Reveal"/></p>'
    }
  };

  vGrooveChanged(): void {
    if (this.vGrooveYesNo === true)
    {
      this.project.vGrooveOffset = this.project.availableVGrooveOffsets ? this.project.availableVGrooveOffsets.min : environment.vGroove.offset.openingEdgeMinOffset;
    }
    else
    {
      this.project.vGrooveOffset = 0;
    }
  }

  infoClicked($event, subject: string) {
    $event.stopPropagation();

    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    const instance = dialogRef.componentInstance;

    instance.data = this.infoData[subject];
  }

  openMatFilters($event) {

    $event.stopPropagation();

    const config = new MatDialogConfig();

    config.backdropClass = 'light-backdrop';
    config.maxWidth = '100%';

    const dialogRef = this.dialog.open(FiltersDialogComponent, config);

    const instance = dialogRef.componentInstance;
    instance.productData = this.productCategory.products;
    instance.options = this.productCategory.filterOptions;

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.availableMatProductsChanged(result);

        this.productCategory.filterOptions.forEach(option => {
          option.previousSelection = null;
        });

        //this.scrollPastLargePreview();
      } else {
        instance.revertOptions();
      }
    });
  }

  // update available mats
  availableMatProductsChanged(availableMatProducts: Array<any>) {
    this.productCategory.filteredProducts = availableMatProducts;

    this.currentMatOptions = {
      quality: this.productCategory.filterOptions[0].selection,
      coreType: this.productCategory.filterOptions[1].selection,
      thickness: this.productCategory.filterOptions[2].selection,
      finish: this.productCategory.filterOptions[3].selection
    };

    // if not loading a project
    if (!this.project.originalId && this.project.primaryProduct === 'MAT') {
      const tags = [];
      if (this.currentMatOptions.finish.toLowerCase() !== this.productCategory.filterOptions[3].defaultSelection.toLowerCase()) tags.push(this.currentMatOptions.finish);
      if (this.currentMatOptions.thickness.toLowerCase() !== this.productCategory.filterOptions[2].defaultSelection.toLowerCase() || tags.length > 0) tags.push(this.currentMatOptions.thickness.replace(' ', '-'));
      if (this.currentMatOptions.coreType.toLowerCase() !== this.productCategory.filterOptions[1].defaultSelection.toLowerCase() || tags.length > 0) tags.push(this.currentMatOptions.coreType + '-core');
      tags.push(this.currentMatOptions.quality);

      // TODO: remove the replace piece once we update the DB to not have them
      const subUrl = tags.reverse().join('_') + '/' + availableMatProducts[0].categoryUrl.replace('.html', '');
      this.location.go('/buy/matboards/' + subUrl.toLowerCase());
    }

    this.groupedProducts = this.group(this.productCategory.filteredProducts ? this.productCategory.filteredProducts : this.productCategory.products);
    this.availableProductsChanged.emit({
      numProductGroups: this.groupedProducts.length,
      allSelected: this.groupedProducts[0].products.length === this.groupedProducts[0].products.filter(p => this.isSelected(p)).length,
      missingDependency: this.missingDependency
    });

    // need to replace the whole mat object with a clone of itself
    // so that change detection notices and updates
    this.productCategory = Object.assign({}, this.productCategory);
  }

  get missingDependency(): string {
    const deps = this.layers[this.currentTabIndex].projectDependencies;
    for (const i in deps) {
      if (!this.project[deps[i]]) {
        return deps[i];
      }
    }
    return null;
  }
}
